import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import TinyMceModal from '../../TinyMceModal/TinyMceModal';
import Button from '../../../atoms/Button/Button';
import Textarea from '../../../atoms/Textarea/Textarea';
import TinyMceEditor from '../../../atoms/TinyMceEditor/TinyMceEditor';

// Utils
import cn from '../../../../utils/cn';

// Styles
import styles from './TinyMceField.module.css';
import IconButton from '../../../atoms/IconButton/IconButton';

/**
 * TinyMceField - Wrapper component with textarea and buttons for TinyMCE editor
 * @param {object} props
 * @param {string} [props.value] - Current editor value (HTML)
 * @param {(value: string) => void} props.onChange - Change handler
 * @param {string} [props.name] - Input name attribute
 * @param {boolean} [props.disabled] - Whether editor is disabled
 * @param {boolean} [props.readOnly] - Read-only (posted, not user-editable)
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder] - Placeholder text
 * @param {number} [props.height=400] - Editor height in pixels
 * @param {Function} [props.onBlur] - Blur handler
 * @returns {JSX.Element} TinyMceField component
 */
function TinyMceField({
  value = '',
  onChange,
  name,
  disabled = false,
  readOnly = false,
  error = '',
  className = '',
  placeholder = '',
  height = 500,
  onBlur,
}) {
  const { t } = useTranslation();
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  // Store editor value temporarily while editing in modal
  const tempEditorValueRef = useRef(value);
  // Store initial value when modal opens to detect changes
  const initialValueRef = useRef(value);

  /**
   * Sync tempEditorValueRef with value prop when modal is closed
   */
  useEffect(() => {
    if (!isEditorModalOpen) {
      tempEditorValueRef.current = value;
    }
  }, [value, isEditorModalOpen]);

  /**
   * Handle textarea change
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e - Change event
   */
  const handleTextareaChange = (e) => {
    if (readOnly) return;
    if (onChange) {
      onChange(e.target.value);
    }
  };

  /**
   * Handle editor modal open
   */
  const handleEditorOpen = () => {
    tempEditorValueRef.current = value;
    initialValueRef.current = value; // Store initial value for comparison
    setIsEditorModalOpen(true);
  };

  /**
   * Check if editor content has changed
   * @returns {boolean} True if content has changed
   */
  const hasContentChanged = () => tempEditorValueRef.current !== initialValueRef.current;

  /**
   * Close editor modal without confirmation
   */
  const handleEditorCloseWithoutConfirmation = () => {
    setIsEditorModalOpen(false);
    if (onBlur) {
      onBlur();
    }
  };

  /**
   * Handle editor modal close with confirmation if needed
   */
  const handleEditorClose = () => {
    if (hasContentChanged()) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(t('form.confirmCloseEditor'));
      if (!confirmed) {
        return;
      }
    }
    handleEditorCloseWithoutConfirmation();
  };

  /**
   * Handle editor change in modal
   * @param {string} newValue - New editor value
   */
  const handleEditorChange = (newValue) => {
    tempEditorValueRef.current = newValue;
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    tempEditorValueRef.current = value; // Reset to original value
    handleEditorCloseWithoutConfirmation();
  };

  /**
   * Handle validate button click
   */
  const handleValidate = () => {
    if (onChange) {
      onChange(tempEditorValueRef.current);
    }
    initialValueRef.current = tempEditorValueRef.current;
    handleEditorCloseWithoutConfirmation();
  };

  return (
    <>
      <div
        className={cn([
          styles.wrapper,
          error && styles['has-error'],
          disabled && styles['is-disabled'],
          readOnly && styles['is-read-only'],
          className,
        ])}
        data-name={name}
      >
        <div className={styles['textarea-container']}>
          <Textarea
            value={value || ''}
            onChange={handleTextareaChange}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            hasError={!!error}
            theme="light"
            rows={4}
          />

          <div className={styles['button-group']}>
            {!readOnly && (
              <IconButton
                icon="edit-2"
                onClick={handleEditorOpen}
                title={t('common.edit')}
                color="no-background"
              />
            )}
          </div>
        </div>

        {error && (
          <div className={styles.error} role="alert" aria-live="polite">
            {error}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      <TinyMceModal
        isOpen={isEditorModalOpen}
        onClose={handleEditorClose}
        actionButtons={(
          <>
            <Button
              onClick={handleCancel}
              color="neutral00"
              size="small"
            >
              {t('form.cancel')}
            </Button>
            <Button
              onClick={handleValidate}
              color="primary"
              size="small"
            >
              {t('form.validate')}
            </Button>
          </>
        )}
      >
        <TinyMceEditor
          value={tempEditorValueRef.current}
          onChange={handleEditorChange}
          disabled={disabled}
          height={height}
          placeholder={placeholder}
        />
      </TinyMceModal>
    </>
  );
}

export default TinyMceField;
